<template>
  <div class="flex flex-col gap-md text-on-background">
    <OrInputV3
      :model-value="localState.name"
      type="text"
      size="s"
      :label="$t('queuesTable.name')"
      :placeholder="$t('createQueue.enterName')"
      :error="v$.localState.name.$errors[0]?.$response"
      :loading="v$.localState.name.$pending"
      data-test="current-queue-name-field"
      @update:modelValue="localState.name = $event"
      @blur="v$.localState.name.$touch()"
      @focus="v$.localState.name.$reset()"
    >
      <template #addon>
        <custom-tooltip :text="$t('createQueue.hintName')" />
      </template>
    </OrInputV3>
    <OrInputV3
      v-model="localState.mrt"
      type="text"
      size="s"
      :label="$t('queuesTable.retentionTime')"
      :placeholder="$t('createQueue.enterRetentionTime')"
      :is-invalid="v$.localState.mrt.$error"
      :error="v$.localState.mrt.$errors[0]?.$message"
      data-test="retention-time-input-field"
      @blur="onMrtBlur"
    >
      <template #addon>
        <custom-tooltip :text="$t('createQueue.hintRetentionTime')" />
      </template>
    </OrInputV3>
    <OrSwitchV3
      v-model="localState.redrivePolicy"
      :label="$t('queuesTable.returnPolicy')"
      data-test="return-policy-switcher"
    />
    <div v-if="localState.redrivePolicy">
      <OrInputV3
        v-model="localState.mrc"
        :readonly="!localState.redrivePolicy"
        type="number"
        size="s"
        :label="$t('queuesTable.maxNumberReturns')"
        :placeholder="$t('createQueue.enterMaxNumberReturns')"
        :is-invalid="v$.localState.mrc.$error"
        :error="v$.localState.mrc.$errors[0]?.$message"
        data-test="max-number-of-returns"
        @blur="onMrcBlur"
      >
        <template #addon>
          <custom-tooltip :text="$t('createQueue.hintReturnPolicy')" />
        </template>
      </OrInputV3>
    </div>
  </div>
</template>
<script lang="ts">

import { OrInputV3, OrSwitchV3 } from '@onereach/ui-components';
import { required, minLength, helpers } from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import { get, debouncedWatch } from '@vueuse/core';

import { useLocalState } from '@/use';
import { defineComponent, PropType, computed, watch } from 'vue';
import { CreateQueue, Queue, UpdateQueue } from '../../types';
import QueuesApi from '@/api/QueuesApi';
import { validTime } from '@/utils/validators';
import CustomTooltip from './CustomTooltip.vue';

export default defineComponent({
  name: 'QueuesForm',
  components: {
    OrInputV3,
    OrSwitchV3,
    CustomTooltip,
  },
  props: {
    queue: {
      type: Object as PropType<Queue>,
      required: true,
    },
  },
  emits: ['add', 'update', 'checkName'],
  setup(props) {
    const { localState, isChanged, reset, replace } = useLocalState(props.queue);
    const queueName = computed(() => localState.name);

    debouncedWatch(queueName, async () => {
      const validation = v$.value.localState.name;
      if (await validation.$validate()) validation.$reset();
    }, { debounce: 500 });

    watch(() => props.queue, (queue) => replace(get(queue)), { deep: true });
    const v$ = useVuelidate({ $scope: 'QueuesForm' });
    return {
      v$,
      localState,
      reset,
      isChanged,
      replace,
    };
  },
  validations() {
    return {
      localState: {
        name: {
          name: helpers.withAsync(
            async (val: string) => {
              try {
                if (val === '') {
                  return Promise.reject(this.$t('validation.required'));
                }
                if (val.length < 3) {
                  return Promise.reject(this.$t('validation.minLength'));
                }
                let res;
                if (val !== this.queue.name) {
                  try {res = await QueuesApi.checkQueueName({ queueName: val });}
                  catch (e) {res = '';}
                }
                let notUniqueName = false;
                if (res) notUniqueName = true;
                return Promise[notUniqueName ? 'reject' : 'resolve'](this.$t('validation.alreadyExists'));
              } catch (e) {
                return Promise.reject(e);
              }
            }),
          $lazy: true,
        },
        mrt: {
          required,
          minLengthValue: minLength(2),
          validTime: validTime(this.$t('validation.invalidTime')),
        },
        mrc: {
          required,
        },
      },
    };
  },
  methods: {
    onNameBlur(): void {
      this.v$.localState.name.$touch();
    },

    onMrtBlur(): void {
      this.v$.localState.mrt.$touch();
    },

    onMrcBlur(): void {
      this.v$.localState.mrc.$touch();
    },

    async add(): Promise<void> {
      const value = {
        queueName: this.localState.name,
        queueMrc: this.localState.mrc,
        queueMrt: this.localState.mrt,
        queueRedrive: this.localState.redrivePolicy,
      } as CreateQueue;

      this.$emit('add', value);
    },

    async update(): Promise<void> {
      const value = {
        queueId: this.localState.queueId,
        queueName: this.localState.name,
        queueMrc: this.localState.mrc,
        queueMrt: this.localState.mrt,
        queueRedrive: this.localState.redrivePolicy,
      } as UpdateQueue;

      this.$emit('update', value);
    },
  },
});
</script>
